'use client';
import { load } from '@2gis/mapgl';
import { Map as MapType } from '@2gis/mapgl/types';
import type { GltfPlugin } from '@2gis/mapgl-gltf';
import React, { useEffect, useRef } from 'react';

import {
  MAP_API_KEY,
  MAP_CENTER,
  MAP_OPTIONS,
  MAP_RESTRICTIONS,
} from './const';
import { MODELS } from './models';
import { convertDefaultModelsToBuildingOptions } from './utils';

const CONVERTED_MODELS = MODELS.map(convertDefaultModelsToBuildingOptions);

const CONVERTED_MODELS_IDS = CONVERTED_MODELS.map((model) => model.modelId);

export const MapBlock = () => {
  const gltfPluginRef = useRef<GltfPlugin | undefined>(null);
  const mapRef = useRef<MapType | undefined>(null);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    load('https://mapgl.2gis.com/api/js/v0.0.345').then(async (mapglAPI) => {
      mapRef.current = await new mapglAPI.Map('map-container-gltf', {
        key: MAP_API_KEY,
        center: MAP_CENTER,
        zoom: MAP_OPTIONS.ZOOM,
        pitch: MAP_OPTIONS.PITCH,
        rotation: MAP_OPTIONS.ROTATION,
        minZoom: MAP_RESTRICTIONS.MIN_ZOOM,
        maxZoom: MAP_RESTRICTIONS.MAX_ZOOM,
        lowZoomMaxPitch: 80,
        maxPitch: 70,
        pitchHightLimitation: 46,
        lang: 'en',

        // @ts-ignore
        copyright: true,
        zoomControl: true,

        defaultBackgroundColor: 'rgba(255, 255, 255, 0.94)',
        style: 'c080bb6a-8134-4993-93a1-5b4d8c36a59b',
        styleState: { immersiveRoadsOn: true },
        enableTrackResize: true,
        farTilesLoadingEnabled: true,
      });

      gltfPluginRef.current = await new mapglGltfPlugin.GltfPlugin(
        mapRef.current,
        {
          modelsLoadStrategy: 'waitAll',
        },
      );

      const removeModels = async (ids?: string[]) => {
        if (!ids) return;

        gltfPluginRef?.current?.hideModels(ids);
      };

      const getMapState = () => ({
        zoom: mapRef.current?.getZoom(),
        styleZoom: mapRef.current?.getStyleZoom(),
        pitch: mapRef.current?.getPitch(),
        rotation: mapRef.current?.getRotation(),
        center: mapRef.current?.getCenter(),
      });

      if (mapRef.current) {
        (window as any).mapglInstance = mapRef.current;

        (window as any).getState = getMapState;
      }

      const removeAllModels = () => removeModels(CONVERTED_MODELS_IDS);
      if (!gltfPluginRef?.current) return null;
      gltfPluginRef.current
        .addModels(CONVERTED_MODELS)
        .catch((e: unknown) => {
          console.info(
            '[pageBuilding] Loading model error:',
            CONVERTED_MODELS,
            e,
          );
        })
        .finally(() => {});

      return () => {
        removeAllModels();
        mapRef.current && mapRef?.current.destroy();
        gltfPluginRef.current && gltfPluginRef?.current.destroy();
      };
    });
  }, []);
  return <section id="map-container-gltf" className="h-full w-full" />;
};

MapBlock.displayName = 'MapBlock';
